import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Globe, Phone, ChevronDown } from "lucide-react";
import { NAV_PRIMARY, NAV_MORE, NAV_LINKS } from "@/data/constants";
import { useLanguage } from "@/context/LanguageContext";

export default function Navbar({ settings }) {
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const { lang, toggle, tr } = useLanguage();
  const location = useLocation();
  const moreRef = useRef(null);
  const L = (link) => (lang === "kn" && link.label_kn) || link.label;

  useEffect(() => {
    setOpen(false);
    setMoreOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const isMoreActive = NAV_MORE.some((l) => l.path === location.pathname);

  return (
    <header className="fixed top-0 inset-x-0 z-50 px-3 sm:px-5 pt-3" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto glass-nav rounded-2xl px-4 sm:px-5 py-2.5">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="navbar-logo">
            <div className="h-10 w-10 rounded-xl bg-navy flex items-center justify-center font-display font-black text-white text-lg shrink-0 group-hover:bg-gold transition-colors duration-300">
              G
            </div>
            <div className="leading-tight">
              <p className="font-display font-bold text-navy text-base">Girish Kumar Naidu</p>
              <p className="font-mono text-[9px] text-gold tracking-[0.2em] uppercase">Hoodi Ward 29</p>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-0.5">
            {NAV_PRIMARY.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-3 py-2 rounded-lg text-[13px] font-semibold transition-colors ${
                  location.pathname === link.path ? "text-gold" : "text-navy hover:text-gold"
                }`}
              >
                {L(link)}
              </Link>
            ))}
            <div className="relative" ref={moreRef}>
              <button
                onClick={() => setMoreOpen(!moreOpen)}
                data-testid="nav-more-button"
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-[13px] font-semibold transition-colors ${
                  isMoreActive ? "text-gold" : "text-navy hover:text-gold"
                }`}
              >
                {tr("nav.more")} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${moreOpen ? "rotate-180" : ""}`} />
              </button>
              {moreOpen && (
                <div
                  className="absolute right-0 top-full mt-2 w-52 bg-white border border-gray-200 rounded-xl shadow-xl py-2 overflow-hidden"
                  data-testid="nav-more-menu"
                >
                  {NAV_MORE.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      data-testid={`nav-${link.label.toLowerCase().replace(/\s+|&/g, "-")}`}
                      className={`block px-4 py-2.5 text-[13px] font-semibold transition-colors ${
                        location.pathname === link.path ? "text-gold bg-gold/5" : "text-navy hover:bg-paper hover:text-gold"
                      }`}
                    >
                      {L(link)}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={toggle}
              data-testid="language-toggle"
              className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] font-semibold text-navy hover:text-gold border border-gray-200 transition-colors"
            >
              <Globe className="h-3.5 w-3.5" />
              {lang === "en" ? "ಕನ್ನಡ" : "EN"}
            </button>
            <Link
              to="/submit-ward-issue"
              data-testid="navbar-submit-issue"
              className="hidden md:inline-flex items-center px-4 py-2.5 rounded-full bg-gold text-white font-bold text-[13px] btn-saffron"
            >
              {tr("cta.submitHoodiIssue")}
            </Link>
            <button
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden h-10 w-10 flex items-center justify-center rounded-lg text-navy border border-gray-200"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden border-t border-gray-200 mt-2.5 pt-3 pb-2" data-testid="mobile-menu">
            <nav className="grid grid-cols-2 gap-1">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-3 py-2.5 rounded-lg text-sm font-semibold ${
                    location.pathname === link.path ? "text-gold bg-gold/5" : "text-navy"
                  }`}
                >
                  {L(link)}
                </Link>
              ))}
              <div className="col-span-2 flex items-center gap-2 mt-2">
                <button
                  onClick={toggle}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-3 rounded-lg text-sm font-semibold text-navy border border-gray-200"
                >
                  <Globe className="h-4 w-4" /> {lang === "en" ? "ಕನ್ನಡ" : "English"}
                </button>
                <a
                  href={`tel:${settings?.phone || ""}`}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-3 rounded-lg text-sm font-bold text-white bg-navy"
                >
                  <Phone className="h-4 w-4" /> {tr("cta.callOffice")}
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
